import {Component, EventEmitter, forwardRef, OnInit, Output, ViewChild} from "@angular/core";
import {ControlContainer, ControlValueAccessor, FormGroup, FormGroupDirective, NG_VALUE_ACCESSOR} from "@angular/forms";
import {AttoService} from "@services/atto.service";
import {Observable, of} from "rxjs";
import {FiltroRicercaAttoVO} from "@vo/filtro-ricerca-attoVO";
import {StatoAttoEnum} from "@enum/stato-atto-enum";
import {TipologiaDecretoEnum} from "@enum/tipologia-decreto-enum";
import {PaginazioneVO} from "@vo/paginazioneVO";
import {OrdinamentoVO} from "@vo/ordinamentoVO";
import {map} from "rxjs/operators";
import {AttoVO} from "@vo/attoVO";
import {DatiDecretoManualiVO} from "@vo/dati-decreto-manualiVO";
import {MetadatiRepertorioVO} from "@vo/metadati-repertorioVO";
import {ModaleAggiungiDatiDecretoComponent} from "@app/shared/components/modale-aggiungi-dati-decreto/modale-aggiungi-dati-decreto.component";

@Component({
    selector: "app-select-decreto",
    templateUrl: "./select-decreto.component.html",
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectDecretoComponent), multi: true }]
})
export class SelectDecretoComponent implements OnInit, ControlValueAccessor {
    value: any;
    // Proprietà riprese dal vecchio componente
    formGroup: FormGroup;
    @ViewChild("modaleAggiungiDatiDecreto", { static: false }) modaleAggiungiDatiDecreto: ModaleAggiungiDatiDecretoComponent;
    @Output() decretoModificato = new EventEmitter<void>();

    // Nuove proprietà
    filtroNumero: string = "";
    filtroAnno: string;
    anniDisponibili: number[] = [];
    erroreDecreto: boolean = false;
    erroreDecretoTesto: string = "";
    inserimentoManuale: boolean = false; // true se l'utente ha usato la modale

    constructor(
        private controlContainer: ControlContainer,
        private attoService: AttoService
    ) {}

    ngOnInit() {
        this.formGroup = this.controlContainer.control as FormGroup;
        // Genera lista anni da 2020 ad anno corrente
        const annoCorrente = new Date().getFullYear();
        this.anniDisponibili = [];
        for (let a = annoCorrente; a >= 2020; a--) {
            this.anniDisponibili.push(a);
        }
        this.filtroAnno = String(annoCorrente);
    }

    /**
     * Al blur del campo numero, zero-pad a 7 cifre.
     * Es: "123" → "0000123", "45" → "0000045"
     */
    onNumeroBlur() {
        if (this.filtroNumero) {
            this.filtroNumero = this.filtroNumero.replace(/[^0-9]/g, "");
            if (this.filtroNumero.length > 0 && this.filtroNumero.length <= 7) {
                while (this.filtroNumero.length < 7) {
                    this.filtroNumero = "0" + this.filtroNumero;
                }
            }
        }
        this.erroreDecreto = false;
        this.erroreDecretoTesto = "";
    }

    /**
     * Validazione decreto: chiamata da crea-nota al submit della form.
     * Numero e anno sempre obbligatori. Cerca via API:
     * - trovato → arricchisce dati decreto, ritorna true
     * - non trovato + inserimentoManuale → nessun errore, ritorna true
     * - non trovato + inserimento standard → errore, ritorna false
     */
    validaDecreto(): Observable<boolean> {
        // Se il decreto e gia stato selezionato (da API o da modale), darlo per buono
        if (this.formGroup.get("decreto").value) {
            return of(true);
        }

        if (!this.filtroNumero || !this.filtroAnno) {
            this.erroreDecreto = true;
            this.erroreDecretoTesto = "Compilare numero e anno del decreto";
            // Resetta decreto se era inizializzato con dati non validi
            this.formGroup.get("decreto").setValue(null);
            return of(false);
        }

        const filtro: FiltroRicercaAttoVO = new FiltroRicercaAttoVO();
        const dal: Date = new Date();
        dal.setDate(1);
        dal.setMonth(0);
        dal.setFullYear(parseInt(this.filtroAnno, 10));
        dal.setHours(0);
        dal.setMinutes(0);
        dal.setSeconds(0);
        const al: Date = new Date();
        al.setDate(31);
        al.setMonth(11);
        al.setFullYear(parseInt(this.filtroAnno, 10));
        al.setHours(23);
        al.setMinutes(59);
        al.setSeconds(59);
        filtro.limitazioniVisibilita = true;
        filtro.stato = StatoAttoEnum.PUBBLICATO;
        filtro.numeroRepertorio = parseInt(this.filtroNumero, 10);
        filtro.dataRepertoriazioneDal = dal.toISOString();
        filtro.dataRepertoriazioneAl = al.toISOString();

        const paginazione: PaginazioneVO = new PaginazioneVO();
        paginazione.pageNumber = 0;
        paginazione.size = 1;

        const ordinamento: OrdinamentoVO = new OrdinamentoVO();
        ordinamento.elemento = "dataApertura";
        ordinamento.direzione = 0;

        return this.attoService.ricercaAvanzata(filtro, false, paginazione, ordinamento).pipe(
            map((res) => {
                if (res.resultList && res.resultList.length > 0) {
                    // Decreto trovato: salva tutti i dati aggiuntivi (come il vecchio typeahead)
                    const decreto = res.resultList[0];
                    const decretoSelect = new AttoVO();
                    decretoSelect.id = decreto.id;
                    decretoSelect.oggetto = decreto.oggetto;
                    decretoSelect.metadatiRepertorio = decreto.metadatiRepertorio;
                    decretoSelect.dataConclusione = decreto.dataConclusione;
                    this.formGroup.get("decreto").setValue(decretoSelect);
                    this.erroreDecreto = false;
                    this.erroreDecretoTesto = "";
                    return true;
                }

                if (this.inserimentoManuale) {
                    // Inserimento manuale via modale: non dare errore, fidarsi dell"utente
                    this.erroreDecreto = false;
                    this.erroreDecretoTesto = "";
                    return true;
                } else {
                    // Inserimento standard: decreto non trovato, errore e reset
                    this.erroreDecreto = true;
                    this.erroreDecretoTesto = "Decreto non trovato";
                    this.formGroup.get("decreto").setValue(null);
                    return false;
                }
            })
        );
    }

    aggiungi() {
        this.modaleAggiungiDatiDecreto.openModal();
    }

    /**
     * Chiamato dalla modale quando l'utente conferma i dati decreto inseriti manualmente.
     * Replica il mapping DatiDecretoManualiVO → AttoVO del vecchio componente.
     * Setta inserimentoManuale = true cosi la validazione al submit non blocca se l'API non trova il decreto.
     */
    confermaDatiDecreto(datiDecreto: DatiDecretoManualiVO) {
        this.inserimentoManuale = true;
        const decretoSelect = new AttoVO();
        decretoSelect.oggetto = datiDecreto.oggetto;
        decretoSelect.metadatiRepertorio = new MetadatiRepertorioVO();
        decretoSelect.dataConclusione = datiDecreto.dataConclusione;
        decretoSelect.metadatiRepertorio.numero = datiDecreto.numero;
        decretoSelect.metadatiRepertorio.data = datiDecreto.dataConclusione;
        decretoSelect.metadatiRepertorio.segnatura = datiDecreto.segnatura;
        this.formGroup.get("decreto").setValue(decretoSelect);
        this.formGroup.updateValueAndValidity({
            onlySelf: false,
            emitEvent: true,
        });
        this.decretoModificato.emit();
    }

    onFiltroChange() {
        this.decretoModificato.emit();
    }

    /**
     * Rimuove il decreto selezionato, resetta i campi di ricerca e il flag inserimentoManuale.
     */
    rimuoviDecreto() {
        this.formGroup.get("decreto").setValue(null);
        this.inserimentoManuale = false;
        this.filtroNumero = "";
        this.filtroAnno = String(new Date().getFullYear());
        this.erroreDecreto = false;
        this.erroreDecretoTesto = "";
        this.decretoModificato.emit();
    }



    onChange: any = () => { };
    onTouched: any = () => { };

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(value: any) {
        this.value = value;
    }

    get decretoDisplayNumero(): string {
        const decreto = this.formGroup && this.formGroup.get('decreto') ? this.formGroup.get('decreto').value : null;
        if (decreto && decreto.metadatiRepertorio && decreto.metadatiRepertorio.numero != null) {
            return String(decreto.metadatiRepertorio.numero).padStart(7, '0');
        }
        return this.filtroNumero || '';
    }

    get decretoDisplayAnno(): string {
        const decreto = this.formGroup && this.formGroup.get('decreto') ? this.formGroup.get('decreto').value : null;
        if (decreto) {
            const data = (decreto.metadatiRepertorio && decreto.metadatiRepertorio.data) || decreto.dataConclusione;
            if (data) {
                return String(new Date(data).getFullYear());
            }
        }
        return this.filtroAnno || '';
    }
}
